import { IDirective, IDirectiveFactory, IRootScopeService, IScope, IQService, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IAggregatedReaction, ICreateReaction, ISelectedReaction } from "../models/models";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { ReactionService } from "@gtmhub/experiments/services/reaction.service";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { ICheckinV2 } from "@gtmhub/okrs/metrics";
import { IComment } from "@gtmhub/comments";
import { EmojiButton, EmojiButtonOptions, EmojiSelection } from "@joeattardi/emoji-button";

export interface IReactionWithCommentsBindings {
  target: ICheckinV2 | IComment;
  targetType: string;
  commentsCount: number;
  reactions: IAggregatedReaction[];
  page: "feed" | "metric" | "thread" | "announcements";
  feedId?: string;
  userIds?: string[];
  avatarPreviewNumber?: number;
}

type CreateEmojiButtonFn = (options: EmojiButtonOptions) => EmojiButton;
export interface IReactionWithCommentsScope extends IScope, IReactionWithCommentsBindings {
  openEmojiPicker(): void;

  canReact: boolean;

  assigneesIdMap: IAssigneeIdMap;
  targetTypes: {
    checkin: string;
    comment: string;
    announcement: string;
    feed: string;
  };

  onPageUsed: {
    feed: boolean;
    metric: boolean;
    thread: boolean;
    announcements: boolean;
  };

  reactUpdate(selectedReaction: ISelectedReaction, targetId: string, targetType: string): void;
  action(): void;
}

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assigneesIdMap: state.assignees.map,
});

export class ReactionWithComments implements IDirective {
  public scope = {
    target: "=",
    targetType: "@",
    commentsCount: "@",
    reactions: "=",
    page: "@",
    feedId: "@?",
    userIds: "<?",
    avatarPreviewNumber: "<?",
  };

  public restrict = "";
  public templateUrl = require("../views/reactions-with-comments.jade");

  private emojiButton: EmojiButton = null;
  private maxReactionsPerItem = 23;
  private disableReactUpdate = false;

  constructor(
    private $rootScope: IRootScopeService,
    private $state: StateService,
    private $q: IQService,
    private reactionService: ReactionService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux
  ) {}

  public static factory(): IDirectiveFactory {
    const directive = (
      $rootScope: IRootScopeService,
      $state: StateService,
      $q: IQService,
      reactionService: ReactionService,
      uiErrorHandlingService: UIErrorHandlingService,
      assigneeActions: AssigneeActions,
      $ngRedux: INgRedux
    ) => new ReactionWithComments($rootScope, $state, $q, reactionService, uiErrorHandlingService, assigneeActions, $ngRedux);

    directive.$inject = ["$rootScope", "$state", "$q", "ReactionService", "UIErrorHandlingService", "AssigneeActions", "$ngRedux"];
    return directive;
  }

  public link(scope: IReactionWithCommentsScope): void {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(scope);
    scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    let createEmojiButton: CreateEmojiButtonFn;
    this.getEmojiButton().then((factory) => {
      scope.canReact = true;
      createEmojiButton = factory;
    });

    scope.targetTypes = {
      checkin: "checkin",
      comment: "comment",
      announcement: "announcement",
      feed: "feed",
    };

    scope.onPageUsed = {
      feed: scope.page === "feed",
      metric: scope.page === "metric",
      thread: scope.page === "thread",
      announcements: scope.page === "announcements",
    };

    const react = (selectedReaction: ISelectedReaction, targetId: string, targetType: string): void => {
      if (scope.reactions.length >= this.maxReactionsPerItem) {
        const error: IUIError = {
          title: "You reached the maximum number of reactions.",
          message: "Emotions… we all have them. For performance reasons however, we set a limit of 23 reactions.",
          body: "",
          connectionError: true,
        };

        this.uiErrorHandlingService.handleModal(error);
        return;
      }

      const createReaction: ICreateReaction = {
        ...selectedReaction,
        targetId,
        targetType,
      };

      this.reactionService.createReaction(createReaction, !!scope.target.gif?.id).then(
        (newReaction) => {
          this.$rootScope.$broadcast("reactionCreated", newReaction);
          this.disableReactUpdate = false;
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(error);
          this.disableReactUpdate = false;
        }
      );
    };

    const unreact = (reaction: IAggregatedReaction, targetId: string): void => {
      // added for the feeds as they don't contain that information in the entity
      reaction.targetId = targetId;
      reaction.targetType = scope.targetType;

      this.reactionService.deleteReaction(reaction.name, targetId).then(
        () => {
          this.$rootScope.$broadcast("reactionDeleted", reaction);
          this.disableReactUpdate = false;
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(error);
          this.disableReactUpdate = false;
        }
      );
    };

    scope.reactUpdate = (selectedReaction: ISelectedReaction, targetId: string, targetType: string): void => {
      if (this.disableReactUpdate) {
        return;
      }

      this.disableReactUpdate = true;
      let foundedReaction: IAggregatedReaction = null;

      if (scope.reactions && Object.keys(scope.reactions).length > 0) {
        foundedReaction = scope.reactions.find((reaction) => reaction.name.toLowerCase() === selectedReaction.name);
      } else {
        scope.reactions = [];
      }

      if (foundedReaction && foundedReaction.currentUserReacted) {
        unreact(foundedReaction, targetId);
      } else {
        react(selectedReaction, targetId, targetType);
      }
    };

    scope.action = (): void => {
      if (scope.onPageUsed.metric) {
        this.$state.go("._message.thread", { targetId: scope.target.id, targetType: "checkin" });
      }
      if (scope.onPageUsed.feed && scope.feedId) {
        this.$state.go("._comment.thread", { targetId: scope.feedId });
      }
    };

    const createEmojiPickerInstance = () => {
      if (this.emojiButton) {
        return;
      }

      this.emojiButton = createEmojiButton({
        position: "bottom-start",
        showVariants: false,
        showAnimation: false,
      });
    };

    const toggleEmojiPicker = (element: HTMLElement): void => {
      this.emojiButton.togglePicker(element);
    };

    const onEmojiSelected = (): void => {
      this.emojiButton.on("emoji", (emojiSelection: EmojiSelection) => {
        // emoji name should be with "-" not with " ".
        const selectedReaction: ISelectedReaction = {
          name: emojiSelection.name.replace(/\s+/g, "-").toLowerCase(),
          emoji: emojiSelection.emoji,
        };

        scope.reactUpdate(selectedReaction, scope.target.id, scope.targetType);
        destroyEmojiPicker();
      });
    };

    const destroyEmojiPicker = (): void => {
      this.emojiButton.destroyPicker();
      this.emojiButton = null;
    };

    scope.openEmojiPicker = (): void => {
      const isPickerVisible = this.emojiButton ? this.emojiButton.isPickerVisible() : false;
      const htmlElement = document.getElementById(`emoji-picker-${scope.page}-${scope.target.id}`);

      if (isPickerVisible) {
        destroyEmojiPicker();
      } else {
        /*  we want to destroy the current instance and create a new one
            because if the user clicks on another button,
            the instance will be for the previous one button */
        if (this.emojiButton) {
          destroyEmojiPicker();
        }

        createEmojiPickerInstance();
        toggleEmojiPicker(htmlElement);
        onEmojiSelected();
      }
    };

    const onScopeDestroy = () => {
      if (this.emojiButton) {
        this.emojiButton.off("emoji", () => {
          // removes the on listener for the emoji event
        });

        destroyEmojiPicker();
      }
    };

    scope.$on("$destroy", onScopeDestroy);
  }

  private getEmojiButton(): IPromise<CreateEmojiButtonFn> {
    const depDefer = this.$q.defer<CreateEmojiButtonFn>();
    import(
      /* webpackChunkName: "emoji-button" */
      "@joeattardi/emoji-button"
    ).then((dep) => depDefer.resolve((options: EmojiButtonOptions) => new dep.EmojiButton(options)), depDefer.reject);
    return depDefer.promise;
  }
}
